import React, { PropsWithChildren } from 'react';
import {
  Grid,
  GridProps,
  RadioGroup,
  RadioGroupProps,
  SelectProps,
  SwitchProps,
  TextFieldProps,
} from '@material-ui/core';
import { Controller, ControllerProps, ControllerRenderProps, FieldValues } from 'react-hook-form';
import {
  Checkbox,
  FileUpload,
  IUploadProps,
  Select,
  Switch,
  TextField,
  Typography,
} from '@dls/react-core';
import { KeyboardDatePickerProps } from '@material-ui/pickers';

import { KeyboardDatePicker } from '@dls/react-mui-datepicker';
import { Paper } from '@dls/react-mui-paper';
import { Icon } from '@dls/react-icon';
import styled from 'styled-components';
import componentColor, { dls } from '@dls/react-colors';
import { Checkmark16 } from '@dls/react-icons';
import { em } from '@dls/react-theme';

type ControlledInputProps<T extends FieldValues> = Omit<ControllerProps<T>, 'render'> & {
  inputProps?: Omit<TextFieldProps, keyof ControllerRenderProps | 'error'>;
};

export const ControlledInput = <T extends FieldValues>({
  inputProps,
  ...controllerProps
}: ControlledInputProps<T>) => (
  <Controller
    {...controllerProps}
    render={({ field, fieldState: { error } }) => (
      <TextField
        data-testid={controllerProps.name}
        {...inputProps}
        {...field}
        error={!!error}
        helperText={error?.message}
      />
    )}
  />
);

type ControlledSwitchProps<T extends FieldValues> = Omit<ControllerProps<T>, 'render'> & {
  inputProps?: Omit<SwitchProps, keyof ControllerRenderProps | 'error'>;
};

export const ControlledSwitch = <T extends FieldValues>({
  inputProps,
  ...controllerProps
}: ControlledSwitchProps<T>) => (
  <Controller
    defaultValue={false as any}
    {...controllerProps}
    render={({ field: { value, ...otherFields }, fieldState: { error } }) => (
      <Switch
        data-testid={controllerProps.name}
        {...inputProps}
        {...otherFields}
        checked={value as boolean}
      />
    )}
  />
);

type ControlledSelectProps<T extends FieldValues> = PropsWithChildren<
  Omit<ControllerProps<T>, 'render'> & {
    selectProps?: Omit<SelectProps, keyof ControllerRenderProps | 'error' | 'children'>;
  }
>;

export const ControlledSelect = <T extends FieldValues>({
  selectProps,
  children,
  ...controllerProps
}: ControlledSelectProps<T>) => (
  <Controller
    {...controllerProps}
    render={({ field, fieldState: { error } }) => {
      return (
        <>
          <Select
            data-testid={controllerProps.name}
            {...field}
            {...selectProps}
            value={field.value || ''}
            error={!!error}
            className={'DLSDropdown'}
            style={selectProps?.fullWidth ? { width: '100%' } : undefined}
          >
            {children}
          </Select>
          {error && (
            <ErrorMessage className="MuiFormHelperText-root Mui-error">
              {error.message}
            </ErrorMessage>
          )}
        </>
      );
    }}
  />
);

type ControlledRadioGroupProps<T extends FieldValues> = PropsWithChildren<
  Omit<ControllerProps<T>, 'render'> & {
    radioGroupProps?: Omit<RadioGroupProps, keyof ControllerRenderProps | 'children'>;
  }
>;

export const ControlledRadioGroup = <T extends FieldValues>({
  radioGroupProps,
  children,
  ...controllerProps
}: ControlledRadioGroupProps<T>) => (
  <Controller
    {...controllerProps}
    render={({ field, fieldState: { error } }) => (
      <RadioGroup {...radioGroupProps} {...field}>
        {children}
      </RadioGroup>
    )}
  />
);

type ControlledCheckboxGroupProps<T extends FieldValues> = Omit<ControllerProps<T>, 'render'> & {
  options: Record<string, string>;
};

export const ControlledCheckboxGroup = <T extends FieldValues>({
  options,
  ...controllerProps
}: ControlledCheckboxGroupProps<T>) => (
  <Controller
    {...controllerProps}
    render={({ field, fieldState }) => (
      <>
        {fieldState.error && (
          <Grid item xs={12}>
            <ErrorMessage className="MuiFormHelperText-root Mui-error">
              {fieldState.error.message}
            </ErrorMessage>
          </Grid>
        )}
        {Object.entries(options).map(([k, v]) => (
          <Grid item container xs={6}>
            <Checkbox
              name={field.name}
              value={v}
              checked={field.value.includes(v)}
              onChange={(event, checked) => {
                if (checked) {
                  field.onChange([...field.value, event.target.value]);
                } else {
                  field.onChange(field.value.filter((v: string) => v !== event.target.value));
                }
              }}
              checkedIcon={<Checkmark16 />}
            />
            <span>{k}</span>
          </Grid>
        ))}
      </>
    )}
  />
);

export type ControlledDatePickerProps<T extends FieldValues> = PropsWithChildren<
  Omit<ControllerProps<T>, 'render'> & {
    datePickerProps?: Omit<
      KeyboardDatePickerProps,
      keyof ControllerRenderProps | 'error' | 'children'
    >;
  }
>;

export const DLSKeyboardDatePicker = React.forwardRef((props: KeyboardDatePickerProps, ref) => (
  <KeyboardDatePicker
    inputRef={ref}
    format="dd-MM-yyyy"
    fullWidth
    disableToolbar
    autoOk
    variant="inline"
    PopoverProps={{ PaperProps: { className: 'DlsDatePicker', component: Paper } }}
    keyboardIcon={<Icon name="Calendar" size="m" />}
    leftArrowIcon={<Icon name="NavigationLeft" size="m" />}
    rightArrowIcon={<Icon name="NavigationRight" size="m" />}
    {...props}
  />
));

export const ControlledDatePicker = <T extends FieldValues>({
  datePickerProps,
  children,
  ...controllerProps
}: ControlledDatePickerProps<T>) => (
  <Controller
    {...controllerProps}
    render={({ field, fieldState: { error } }) => (
      <DLSKeyboardDatePicker
        data-testid={controllerProps.name}
        format="dd-MM-yyyy"
        {...datePickerProps}
        {...field}
        value={field.value === undefined ? null : (field.value as Date)}
        error={!!error}
        helperText={error?.message}
      />
    )}
  />
);

export type ControlledFileUploadProps<T extends FieldValues> = PropsWithChildren<
  Omit<ControllerProps<T>, 'render'> & {
    fileUploadProps: Omit<
      IUploadProps,
      keyof ControllerRenderProps | 'error' | 'children' | 'onDelete' | 'onFileDrop' | 'files'
    >;
  }
>;

const ErrorMessage = styled.p`
  && {
    font-family: CentraleSans, Tahoma, Arial, Helvetica, sans-serif;
    &.Mui-error {
      height: ${em(18)};
      color: ${componentColor(dls.textBox.text.default.validated)};
    }
  }
`;

export const ControlledFileUpload = <T extends FieldValues>({
  fileUploadProps,
  ...controllerProps
}: ControlledFileUploadProps<T>) => (
  <Controller
    {...controllerProps}
    render={({ field, fieldState: { error } }) => (
      <>
        <FileUpload
          {...fileUploadProps}
          files={field.value || []}
          onDelete={(file) => {
            const files = (field.value as any[]).filter(function (_file: { path: any }) {
              return _file.path !== file.path;
            });
            field.onChange([...files]);
          }}
          onFileDrop={(files, retry) => {
            field.onChange(files);
          }}
          {...field}
        />
        {error && (
          <ErrorMessage className="MuiFormHelperText-root Mui-error">{error.message}</ErrorMessage>
        )}
      </>
    )}
  />
);

const StyledFormItem = styled(Typography)`
  display: flex;
  flex-direction: column;
  row-gap: 8px;
`;

export const GridFormElement = ({
  label,
  children,
  ...gridProps
}: React.PropsWithChildren<{ label: string }> & GridProps) => {
  return (
    <Grid item xs={12} md={6} {...gridProps}>
      <StyledFormItem>
        <Typography>{label}</Typography>
        {children}
      </StyledFormItem>
    </Grid>
  );
};
